import { useState, useEffect, useContext } from "react";
import { Button, Flex, Modal, Typography, App, Pagination } from "antd";

import PlusIcon from "@/icons/PlusIcon";

import PositionsTable from "@/components/tables/PositionsTable";
import PositionModal from "@/components/modals/PositionModal";
import Breadcrumbs from "@/components/ui/Breadcrumbs";

import { UserContext } from "@/context/UserContext";

import { useTableAutoHeight } from "@/hooks/useTableAutoHeight";

import { getList, deleteItem } from "@/api/positions";

const { Title } = Typography;

const LOCAL_STORAGE_KEY = "positionsTableParams";

// Страница должностей
// Содержит список должностей с возможностью добавления, редактирования и удаления
const Positions = () => {
  const { message } = App.useApp();

  const { user } = useContext(UserContext);

  // Данные
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Инициализация пагинации из localStorage
  const initialTableParams = JSON.parse(
    localStorage.getItem(`${user.user_uuid}_${LOCAL_STORAGE_KEY}`)
  ) || {
    pagination: {
      current: 1,
      pageSize: 10,
      placement: ["bottomLeft"],
      showSizeChanger: true,
    },
  };

  const [tableParams, setTableParams] = useState(initialTableParams);

  // Автоматическая высота таблицы
  const { tableContainerRef, tableHeight, headerHeight } = useTableAutoHeight([
    data,
    tableParams.pagination.pageSize,
  ]);

  // Окно создания/редактирования
  const [isModalShow, setIsModalShow] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);

  // Запрос на сервер для получения данных
  const fetchData = async (pagination, sorter) => {
    setIsLoading(true);

    const params = {
      page: pagination?.current || tableParams.pagination.current,
      perpage: pagination?.pageSize || tableParams.pagination.pageSize,
      ...(sorter?.order && {
        sort: `${sorter.order === "descend" ? "-" : ""}${sorter.field}`,
      }),
    };

    try {
      const { positions, total, page, perPage } = await getList(params);
      setData(positions);

      const updatedTableParams = {
        pagination: {
          ...tableParams.pagination,
          total,
          current: page,
          pageSize: perPage,
        },
      };
      setTableParams(updatedTableParams);

      // Сохраняем параметры пагинации в localStorage
      localStorage.setItem(
        `${user.user_uuid}_${LOCAL_STORAGE_KEY}`,
        JSON.stringify(updatedTableParams)
      );
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setIsLoading(false);
    }
  };

  //  initial fetch
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, []);

  const handleTableChange = async (pagination, filters, sorter) => {
    fetchData(pagination, sorter);
  };

  const handlePaginationChange = (current, pageSize) => {
    // При изменении pageSize сбрасываем страницу на 1
    const shouldResetPage = pageSize !== tableParams.pagination.pageSize;

    const newPagination = {
      ...tableParams.pagination,
      current: shouldResetPage ? 1 : current,
      pageSize,
    };

    fetchData(newPagination, tableParams.sorter);
  };

  // Обработчик добавления новой должности
  const handleAddNew = () => {
    setCurrentItem(null);
    setIsModalShow(true);
  };

  // Обработчик редактирования должности
  const handleEditItem = (item) => {
    setCurrentItem(item);
    setIsModalShow(true);
  };

  // Обработчик сохранения должности
  const handleSaveItem = () => {
    setIsModalShow(false);
    if (currentItem) {
      fetchData();
    } else {
      fetchData({ current: 1 });
    }
  };

  // Обработчик удаления должности
  const handleDeleteItem = (item) => {
    Modal.confirm({
      width: 510,
      title: item.can_be_marked_deleted
        ? `Отметить должность ${item.name} как удаленную?`
        : `Вы уверены, что хотите удалить должность ${item.name}?`,
      content: item.can_be_marked_deleted
        ? "Эта должность будет отмечена как удаленная, но не будет удалена из системы полностью, т.к. она используется в удаленных сотрудниках."
        : "Это приведет к её полному удалению из системы.",
      onOk() {
        handleConfirmDelete(item);
      },
      okText: "Удалить",
      cancelText: "Отмена",
    });
  };

  // Действительно удаляем должность после подтверждения
  const handleConfirmDelete = async (item) => {
    setIsLoading(true);
    try {
      await deleteItem({ uuid: item.uuid });
      await fetchData();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
      setIsLoading(false);
    }
  };

  return (
    <>
      <Flex vertical gap={25} className="h-100">
        <Flex vertical gap={10}>
          <Breadcrumbs
            items={[
              {
                title: "Административная панель",
              },
              {
                title: "Системные справочники",
              },
              {
                title: "Должности",
              },
            ]}
          />
          <Flex gap={20} align="center" justify="space-between">
            <Title level={2} className="no-margin">
              Должности
            </Title>

            <Button
              type="primary"
              icon={<PlusIcon />}
              onClick={handleAddNew}
            ></Button>
          </Flex>
        </Flex>

        <div ref={tableContainerRef} style={{ flex: 1, minHeight: 0 }}>
          <PositionsTable
            data={data}
            loading={isLoading}
            pagination={false}
            onChange={handleTableChange}
            onEditItem={handleEditItem}
            onDeleteItem={handleDeleteItem}
            scroll={{ x: "100%", y: tableHeight - headerHeight }}
          />
        </div>

        <div style={{ minHeight: 32 }}>
          <Pagination
            {...tableParams.pagination}
            onChange={handlePaginationChange}
          />
        </div>
      </Flex>

      {isModalShow && (
        <PositionModal
          data={currentItem}
          visible={isModalShow}
          onSave={handleSaveItem}
          onCancel={() => setIsModalShow(false)}
        />
      )}
    </>
  );
};

export default Positions;
